import { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ImagePlaceholder } from '@lyearn/icons';
import { HotspotType } from '@lyearn/lygo-blocks';
import { Stack } from '@lyearn/ui';
import ImageWithHotspots from '@/components/ImageWithHotspots';
import UploadButton from '@/modules/classrooms/modules/assignments/homework/cards/components/UploadButton';
import AddCoverDialog from '@/modules/corporate/components/AddCoverDialog';
import { CoverType } from '@/modules/corporate/components/ImageList/types';
import { MediaKindEnum } from '@/types/schema';

import EditorQuestionText from '../../components/Editor/EditorQuestionText';
import QuestionEditorContainer from '../../components/QuestionEditorContainer';
import { QuestionEditorProps } from '../../types';

const ImageHotspotEditor: React.FC<QuestionEditorProps> = (props) => {
  const { question, onChange, viewMode } = props;
  const media = question.attachments?.set?.[0];

  const { t } = useTranslation();
  const [showCoverDialog, { on, off }] = useToggle(false);

  const onSubmit = useCallback(
    (selectedCard?: CoverType) => {
      if (!selectedCard) {
        onChange({
          ...question,
          attachments: { set: [] },
          imageHotspotComponents: { set: [] },
        });
        return;
      }

      const { url, name } = selectedCard;
      onChange({
        ...question,
        attachments: { set: [{ url, name, kind: MediaKindEnum.Image }] },
        imageHotspotComponents: { set: [] },
      });
    },
    [onChange, question],
  );

  const onChangeHotspots = useCallback(
    (hotspotsState: HotspotType[]) => {
      onChange({
        ...question,
        imageHotspotComponents: {
          set: hotspotsState.map(({ x, y, id, caption }) => ({
            x,
            y,
            id,
            text: caption,
            mediaId: media?.mediaId,
          })),
        },
      });
    },
    [media?.mediaId, onChange, question],
  );

  const hotspots = question.imageHotspotComponents?.set.map((hotspot) => ({
    ...hotspot,
    caption: hotspot.text ?? '',
  }));

  const isCreateMode = viewMode === 'create';

  return (
    <QuestionEditorContainer maxWidth={60} {...props}>
      <EditorQuestionText className="mt-8" {...props} />
      <Stack className="mt-8 w-full" direction="col">
        {media ? (
          <ImageWithHotspots
            hotspots={hotspots}
            imageClassName="h-[31rem]"
            media={media}
            popper={{
              className: 'overflow-hidden w-[28rem] h-38',
              placeholder: t('QuizCreator:WriteTheCorrectAnswer'),
              textarea: 'py-10',
            }}
            readOnly={!isCreateMode}
            onChange={onChangeHotspots}
          />
        ) : (
          <UploadButton
            PlaceholderIcon={ImagePlaceholder}
            isDisabled={false}
            placeholder={t('QuizCreator:UploadImageHotspot')}
            supportedMimeTypes=""
            onClick={on}
          />
        )}
      </Stack>
      {showCoverDialog ? <AddCoverDialog cover={media} onClose={off} onSubmit={onSubmit} /> : null}
    </QuestionEditorContainer>
  );
};

export default ImageHotspotEditor;
